import { CompanyType } from '../../../__generated__/graphql'

// Keys for localStorage
const TEASER_COLUMNS_KEY = 'reportPrototype_teaserColumns'
const WEBSITE_COLUMNS_KEY = 'reportPrototype_websiteColumns'

// Default columns (always included)
const getDefaultTeaserColumns = (companyType: string) => {
  const baseColumns = ['name', 'tdImpressions', 'tdClicks', 'reads']
  
  // Add company-specific defaults
  if (companyType === CompanyType.Publisher) {
    return [...baseColumns, 'publisherRevenue', 'conversions']
  } else {
    return [...baseColumns, 'totalCost', 'conversions']
  }
}

const getDefaultWebsiteColumns = (companyType: string) => {
  const baseColumns = ['name', 'tdImpressions', 'tdClicks', 'reads']
  
  // Add company-specific defaults
  if (companyType === CompanyType.Publisher) {
    return [...baseColumns, 'publisherRevenue', 'conversions']
  } else {
    return [...baseColumns, 'totalCost', 'conversions']
  }
}

// Get locked columns that should always be included
const getLockedColumns = () => ['name', 'tdImpressions', 'tdClicks', 'reads']

// Ensure locked columns are always included
const ensureLockedColumns = (columns: string[]) => {
  const locked = getLockedColumns()
  const combined = [...locked, ...columns]
  return Array.from(new Set(combined))
}

// Save teaser column preferences
export const saveTeaserColumnPreferences = (columns: string[], companyType: string) => {
  try {
    const key = `${TEASER_COLUMNS_KEY}_${companyType}`
    const columnsToSave = ensureLockedColumns(columns)
    localStorage.setItem(key, JSON.stringify(columnsToSave))
  } catch (error) {
    console.warn('Failed to save teaser column preferences:', error)
  }
}

// Load teaser column preferences
export const loadTeaserColumnPreferences = (companyType: string): string[] => {
  try {
    const key = `${TEASER_COLUMNS_KEY}_${companyType}`
    const saved = localStorage.getItem(key)
    
    if (saved) {
      const parsed = JSON.parse(saved)
      return ensureLockedColumns(parsed)
    }
  } catch (error) {
    console.warn('Failed to load teaser column preferences:', error)
  }
  
  // Return defaults if no saved preferences or error
  return getDefaultTeaserColumns(companyType)
}

// Save website column preferences
export const saveWebsiteColumnPreferences = (columns: string[], companyType: string) => {
  try {
    const key = `${WEBSITE_COLUMNS_KEY}_${companyType}`
    const columnsToSave = ensureLockedColumns(columns)
    localStorage.setItem(key, JSON.stringify(columnsToSave))
  } catch (error) {
    console.warn('Failed to save website column preferences:', error)
  }
}

// Load website column preferences
export const loadWebsiteColumnPreferences = (companyType: string): string[] => {
  try {
    const key = `${WEBSITE_COLUMNS_KEY}_${companyType}`
    const saved = localStorage.getItem(key)
    
    if (saved) {
      const parsed = JSON.parse(saved)
      return ensureLockedColumns(parsed)
    }
  } catch (error) {
    console.warn('Failed to load website column preferences:', error)
  }
  
  // Return defaults if no saved preferences or error
  return getDefaultWebsiteColumns(companyType)
}

// Utility to filter out invalid columns based on company type
export const filterValidColumns = (columns: string[], companyType: string): string[] => {
  const invalidForPublisher = ['totalCost', 'cpa']
  const invalidForAdvertiser = ['publisherRevenue', 'rpa']
  
  if (companyType === CompanyType.Publisher) {
    return columns.filter(col => !invalidForPublisher.includes(col))
  } else {
    return columns.filter(col => !invalidForAdvertiser.includes(col))
  }
}

// Clear all column preferences (for testing or reset)
export const clearColumnPreferences = () => {
  try {
    const storage = localStorage as Storage & { keys?: () => string[] | null }
    const collectedKeys: string[] = (() => {
      if (typeof storage.keys === 'function') {
        const keys = storage.keys()
        if (Array.isArray(keys)) {
          return keys
        }
      }

      if (typeof storage.length === 'number' && typeof storage.key === 'function') {
        const length = storage.length
        const dynamicKeys: string[] = []
        for (let index = 0; index < length; index += 1) {
          const key = storage.key(index)
          if (key) {
            dynamicKeys.push(key)
          }
        }
        if (dynamicKeys.length > 0) {
          return dynamicKeys
        }
      }

      return Object.keys(storage)
    })()

    const keys = collectedKeys.filter(key => 
      key.startsWith(TEASER_COLUMNS_KEY) || key.startsWith(WEBSITE_COLUMNS_KEY)
    )
    keys.forEach(key => localStorage.removeItem(key))
  } catch (error) {
    console.warn('Failed to clear column preferences:', error)
  }
}
